import 'edcom-ui/global.less';
import 'edcom-ui/styles/flexbox.less';
import express from 'express'; // Used only for typings
import React, { useEffect } from 'react';
import clsx from 'clsx'
import App from 'next/app';
import dynamic from 'next/dynamic';
import { AppContext, AppInitialProps, AppProps } from 'next/app';
import { useRouter } from 'next/router';
import StandardLayout from '../components/layouts/StandardLayout';
import RequestContext, {getRequestDataFromRequest, RequestData} from '../services/RequestContext';
import { AccountProvider } from '../services/AccountContext';
import { GDPRProvider } from '../services/GDPRContext';
import { NotificationProvider } from '../services/NotificationContext';
import { PlayersProvider } from '../services/PlayersContext';
import { trackGARouteChange } from '../services/track';
import getConfig from 'next/config';
import '../edcom-ui/styles/flatpickr.less'

type WrappedRequestData = {
  requestData: RequestData;
};

// NextJS incorrectly types the AppContext.ctx.req as http.IncomingRequest instead of express.Request
type WrappedAppContext = AppContext & {
  ctx: {
    req: express.Request;
  };
};

// Show debug info on non-prod environments
const {
  publicRuntimeConfig: { APP_ENV },
} = getConfig();
const EnvironmentInfo = dynamic(
  () =>
    Promise.resolve(
      APP_ENV !== 'prod' ? import('../components/EnvironmentInfo') : null
    ),
  { ssr: false }
);

function WrappedApp(props: AppProps & WrappedRequestData): React.ReactElement {
  const { Component, requestData, pageProps: { pageKey, navProps, footerProps, ...pageProps}  } = props;
  const [a11y, setA11y] = React.useState(true);

  const fixCidHref = (el: HTMLAnchorElement): void => {
    // If target has an href and data-cid property and does not already have a cid in the href
    if (
      el.href &&
      el.dataset &&
      el.dataset.cid &&
      !el.href.match(/[?&]cid=/)
    ) {
      // Add data-cid value to the href querystring
      el.href +=
        (el.href.match(/\?/) ? '&' : '?') + 'cid=' + el.dataset.cid;
    }
  };

  const handleMouseDown: React.MouseEventHandler = (event): void => {
    setA11y(false);
    fixCidHref(event.target as HTMLAnchorElement);
  };

  const handleKeyDown: React.KeyboardEventHandler = () => {
    setA11y(true);
  };

  // Important! Only render the error page in the event that one of our components was the source
  // of the error to prevent infinite rendering loops
  if (Component.displayName === 'Error') {
    return <Component {...pageProps} />;
  }

  // Send route change to GA and GTM.
  const router = useRouter();
  useEffect(() => {
    // document.title was not updating consistently because of a NextJS issue. Accessing document.title inside a
    // timeout in routeChangeComplete is a workaround. For more info: https://github.com/vercel/next.js/issues/6025
    setTimeout(() => {
      trackGARouteChange(router.asPath, document.title);
    }, 0);
  }, [router.asPath]);

  return (
    <div
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      className={clsx({'a11y-disable': !a11y})}
    >
      <RequestContext.Provider value={requestData}>
        <GDPRProvider>
          <AccountProvider>
            <NotificationProvider>
              <PlayersProvider>
                <StandardLayout pageKey={pageKey} navProps={navProps} footerProps={footerProps}>
                  <Component {...pageProps} />
                </StandardLayout>
                <EnvironmentInfo />
              </PlayersProvider>
            </NotificationProvider>
          </AccountProvider>
        </GDPRProvider>
      </RequestContext.Provider>
    </div>
  );
}

WrappedApp.getInitialProps = async (
  appContext: WrappedAppContext
): Promise<AppInitialProps & WrappedRequestData> => {
  const requestData = getRequestDataFromRequest(appContext?.ctx?.req);

  const appProps = await App.getInitialProps(appContext);

  return { ...appProps, requestData };
};

export default WrappedApp;
